import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";
import { setSuperAdminLastTenant } from "../../../../helpers/super_admin_last_tenant";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import {
  createTenant,
  getTenant,
  getTenants,
  impersonate,
  syncTenantSubscriptionPayment,
  Tenant,
  TenantBlockedFilter,
  TenantListOrder,
  TenantListSort,
  TenantSubscriptionStatus,
  toggleTenantBlock,
  updateTenantSubscription,
} from "../../../../services/tenantService";
import { getPlans, Plan } from "../../../../services/planService";
import { startImpersonation } from "../../../../services/authService";
import { updateTenantNurtureOptOut } from "../../../../services/superAdmin/csNurtureService";
import { PaginationMeta } from "../../../../types/api";
import TenantDetailOffcanvas from "./TenantDetailOffcanvas";

const PER_PAGE = 15;
const SEARCH_DEBOUNCE_MS = 350;
/** Tenants without login for this many days get a visual highlight (CS ops). */
const INACTIVE_LOGIN_DAYS = 14;

const isInactiveLogin = (tenant: Tenant, days = INACTIVE_LOGIN_DAYS): boolean => {
  if (!tenant.last_login_at) {
    return true;
  }

  const last = new Date(tenant.last_login_at).getTime();
  if (Number.isNaN(last)) {
    return true;
  }

  const threshold = Date.now() - days * 24 * 60 * 60 * 1000;
  return last < threshold;
};

interface BlockFormValues {
  block_reason: string;
}

interface CreateTenantFormValues {
  studio_name: string;
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  plan_id: string;
  locale: "pt-BR" | "en" | "es";
  currency_code: "BRL" | "USD";
  coupon_code: string;
}

const isTenantBlocked = (tenant: Tenant): boolean => tenant.blocked_at !== null;

const isImpersonationEligible = (tenant: Tenant): boolean => {
  if (!tenant.owner_user) {
    return false;
  }

  if (isTenantBlocked(tenant)) {
    return false;
  }

  const status = tenant.subscription?.status;

  return status === "active" || status === "past_due";
};

const impersonationUnavailableKey = (tenant: Tenant): string | null => {
  if (!tenant.owner_user) {
    return "admin.tenants.impersonate.unavailable.noOwner";
  }

  if (isTenantBlocked(tenant)) {
    return "admin.tenants.impersonate.unavailable.blocked";
  }

  const status = tenant.subscription?.status;

  if (status !== "active" && status !== "past_due") {
    return "admin.tenants.impersonate.unavailable.subscription";
  }

  return null;
};

const subscriptionBadgeColor = (
  status: TenantSubscriptionStatus
): string => {
  switch (status) {
    case "active":
      return "success";
    case "past_due":
      return "warning";
    case "suspended":
      return "danger";
    case "canceled":
      return "secondary";
    default:
      return "secondary";
  }
};

const parseSubscriptionStatusParam = (
  raw: string | null
): "" | TenantSubscriptionStatus => {
  if (
    raw === "active" ||
    raw === "past_due" ||
    raw === "suspended" ||
    raw === "canceled"
  ) {
    return raw;
  }

  return "";
};

const parseBlockedFilterParam = (raw: string | null): TenantBlockedFilter => {
  if (raw === "1" || raw === "true" || raw === "blocked") {
    return "blocked";
  }

  if (raw === "0" || raw === "false" || raw === "operational") {
    return "operational";
  }

  return "all";
};

const SuperAdminTenants = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState<
    "" | TenantSubscriptionStatus
  >(() => parseSubscriptionStatusParam(searchParams.get("subscription_status")));
  const [blockedFilter, setBlockedFilter] = useState<TenantBlockedFilter>(() =>
    parseBlockedFilterParam(searchParams.get("blocked"))
  );
  const [planFilter, setPlanFilter] = useState("");
  const [sort, setSort] = useState<TenantListSort>(() => {
    const raw = searchParams.get("sort");
    return raw === "name" || raw === "last_login_at" || raw === "created_at"
      ? raw
      : "created_at";
  });
  const [order, setOrder] = useState<TenantListOrder>(() =>
    searchParams.get("order") === "asc" ? "asc" : "desc"
  );
  const [filterPlans, setFilterPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [blockTarget, setBlockTarget] = useState<Tenant | null>(null);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [unblockTarget, setUnblockTarget] = useState<Tenant | null>(null);
  const [unblockModalOpen, setUnblockModalOpen] = useState(false);
  const [impersonateTarget, setImpersonateTarget] = useState<Tenant | null>(null);
  const [impersonateModalOpen, setImpersonateModalOpen] = useState(false);
  const [impersonatingUserId, setImpersonatingUserId] = useState<number | null>(
    null
  );
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [creating, setCreating] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTenant, setDetailTenant] = useState<Tenant | null>(null);
  const [subscriptionBusy, setSubscriptionBusy] = useState(false);
  const deepLinkHandledRef = useRef<string | null>(null);

  const notAvailable = t("admin.tenants.common.notAvailable");
  const hasActiveFilters =
    search.trim() !== "" ||
    subscriptionStatusFilter !== "" ||
    blockedFilter !== "all" ||
    planFilter !== "";

  const formatDateTime = useCallback(
    (value: string | null | undefined): string => {
      if (!value) {
        return notAvailable;
      }

      try {
        return new Intl.DateTimeFormat(
          i18n.language === "sp" ? "es" : i18n.language,
          {
            dateStyle: "short",
            timeStyle: "short",
          }
        ).format(new Date(value));
      } catch {
        return notAvailable;
      }
    },
    [i18n.language, notAvailable]
  );

  const blockValidationSchema = useMemo(
    () =>
      Yup.object({
        block_reason: Yup.string()
          .trim()
          .required(t("admin.tenants.validation.blockReasonRequired"))
          .max(500, t("admin.tenants.validation.blockReasonMax")),
      }),
    [t]
  );

  const createValidationSchema = useMemo(
    () =>
      Yup.object({
        studio_name: Yup.string()
          .trim()
          .required(t("admin.tenants.create.validation.studioRequired")),
        name: Yup.string()
          .trim()
          .required(t("admin.tenants.create.validation.nameRequired")),
        email: Yup.string()
          .email(t("admin.tenants.create.validation.emailInvalid"))
          .required(t("admin.tenants.create.validation.emailRequired")),
        password: Yup.string()
          .min(8, t("admin.tenants.create.validation.passwordMin"))
          .required(t("admin.tenants.create.validation.passwordRequired")),
        password_confirmation: Yup.string()
          .oneOf(
            [Yup.ref("password")],
            t("admin.tenants.create.validation.passwordConfirmed")
          )
          .required(t("admin.tenants.create.validation.passwordConfirmed")),
        plan_id: Yup.string().required(
          t("admin.tenants.create.validation.planRequired")
        ),
        locale: Yup.string().required(),
        currency_code: Yup.string().required(),
        coupon_code: Yup.string().trim(),
      }),
    [t]
  );

  useEffect(() => {
    document.title = t("admin.tenants.pageTitle");
  }, [t, i18n.language]);

  const fetchTenants = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getTenants({
        page,
        per_page: PER_PAGE,
        search: search.trim() || undefined,
        subscription_status: subscriptionStatusFilter || undefined,
        blocked:
          blockedFilter === "all"
            ? undefined
            : blockedFilter === "blocked",
        plan_id: planFilter ? Number(planFilter) : undefined,
        sort,
        order,
      });

      setTenants(response.data);
      setMeta(response.meta);
    } catch (error: any) {
      toast.error(error?.data || t("admin.tenants.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [
    page,
    search,
    subscriptionStatusFilter,
    blockedFilter,
    planFilter,
    sort,
    order,
    t,
  ]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = searchInput.trim();
      setSearch((current) => {
        if (current === next) {
          return current;
        }
        setPage(1);
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    getPlans({ per_page: 100 })
      .then((response) => {
        setFilterPlans(response.data.filter((plan) => plan.is_active));
      })
      .catch(() => {
        /* filter plans are optional; create modal loads again on open */
      });
  }, []);

  useEffect(() => {
    if (!createModalOpen) {
      return;
    }

    getPlans({ per_page: 100 })
      .then((response) => {
        setPlans(response.data.filter((plan) => plan.is_active));
      })
      .catch(() => {
        toast.error(t("admin.tenants.create.toast.plansError"));
      });
  }, [createModalOpen, t]);

  const openTenantDetail = async (tenant: Tenant) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailTenant(tenant);
    setSuperAdminLastTenant({ id: tenant.id, name: tenant.name });

    try {
      const response = await getTenant(tenant.id);
      setDetailTenant(response.data);
      setSuperAdminLastTenant({
        id: response.data.id,
        name: response.data.name,
      });
    } catch (error: any) {
      toast.error(error?.data || t("admin.tenants.detail.toast.loadError"));
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    const raw = searchParams.get("tenant_id");
    if (!raw || deepLinkHandledRef.current === raw) {
      return;
    }

    const tenantId = Number(raw);
    if (!Number.isFinite(tenantId) || tenantId <= 0) {
      return;
    }

    deepLinkHandledRef.current = raw;

    const openFromQuery = async () => {
      setDetailOpen(true);
      setDetailLoading(true);
      setDetailTenant(null);

      try {
        const response = await getTenant(tenantId);
        setDetailTenant(response.data);
        setSuperAdminLastTenant({
          id: response.data.id,
          name: response.data.name,
        });
      } catch (error: any) {
        toast.error(error?.data || t("admin.tenants.detail.toast.loadError"));
        setDetailOpen(false);
      } finally {
        setDetailLoading(false);
        const next = new URLSearchParams(searchParams);
        next.delete("tenant_id");
        setSearchParams(next, { replace: true });
      }
    };

    void openFromQuery();
  }, [searchParams, setSearchParams, t]);

  const closeTenantDetail = () => {
    setDetailOpen(false);
    setDetailTenant(null);
    setDetailLoading(false);
  };

  useEffect(() => {
    const next = new URLSearchParams(searchParams);

    if (subscriptionStatusFilter) {
      next.set("subscription_status", subscriptionStatusFilter);
    } else {
      next.delete("subscription_status");
    }

    if (blockedFilter === "blocked") {
      next.set("blocked", "1");
    } else if (blockedFilter === "operational") {
      next.set("blocked", "0");
    } else {
      next.delete("blocked");
    }

    if (
      next.get("subscription_status") === searchParams.get("subscription_status") &&
      next.get("blocked") === searchParams.get("blocked")
    ) {
      return;
    }

    setSearchParams(next, { replace: true });
  }, [subscriptionStatusFilter, blockedFilter, searchParams, setSearchParams]);

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setSubscriptionStatusFilter("");
    setBlockedFilter("all");
    setPlanFilter("");
    setSort("created_at");
    setOrder("desc");
    setPage(1);
  };

  const toggleSort = (column: TenantListSort) => {
    if (sort === column) {
      setOrder((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSort(column);
      setOrder(column === "name" ? "asc" : "desc");
    }
    setPage(1);
  };

  const sortIndicator = (column: TenantListSort): string => {
    if (sort !== column) {
      return "ri-arrow-up-down-line";
    }

    return order === "asc" ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line";
  };

  const refreshDetailIfOpen = async (tenantId: number) => {
    if (!detailOpen) {
      return;
    }

    try {
      const response = await getTenant(tenantId);
      setDetailTenant(response.data);
    } catch {
      /* list refresh still happens */
    }
  };

  const handleExtendCourtesy = async (tenant: Tenant, days: number) => {
    setSubscriptionBusy(true);
    try {
      const response = await updateTenantSubscription(tenant.id, {
        extend_days: days,
        reason: t("admin.tenants.courtesy.reasonExtend", { days }),
      });
      toast.success(response.message);
      await fetchTenants();
      await refreshDetailIfOpen(tenant.id);
    } catch (error: any) {
      toast.error(error?.data || t("admin.tenants.courtesy.toast.error"));
    } finally {
      setSubscriptionBusy(false);
    }
  };

  const handleToggleDemo = async (tenant: Tenant, isDemo: boolean) => {
    setSubscriptionBusy(true);
    try {
      const response = await updateTenantSubscription(tenant.id, {
        is_demo: isDemo,
        reason: isDemo
          ? t("admin.tenants.courtesy.reasonDemoOn")
          : t("admin.tenants.courtesy.reasonDemoOff"),
      });
      toast.success(response.message);
      await fetchTenants();
      await refreshDetailIfOpen(tenant.id);
    } catch (error: any) {
      toast.error(error?.data || t("admin.tenants.courtesy.toast.error"));
    } finally {
      setSubscriptionBusy(false);
    }
  };

  const handleClearCourtesy = async (tenant: Tenant) => {
    setSubscriptionBusy(true);
    try {
      const response = await updateTenantSubscription(tenant.id, {
        clear_courtesy: true,
        reason: t("admin.tenants.courtesy.reasonClear"),
      });
      toast.success(response.message);
      await fetchTenants();
      await refreshDetailIfOpen(tenant.id);
    } catch (error: any) {
      toast.error(error?.data || t("admin.tenants.courtesy.toast.error"));
    } finally {
      setSubscriptionBusy(false);
    }
  };

  const handleMarkSubscriptionPaid = async (tenant: Tenant) => {
    const { value: reason, isConfirmed } = await Swal.fire({
      title: t("admin.tenants.subscription.activate.confirmTitle"),
      html: t("admin.tenants.subscription.activate.confirmBody", {
        name: tenant.name,
      }),
      input: "textarea",
      inputPlaceholder: t(
        "admin.tenants.subscription.activate.reasonPlaceholder"
      ),
      inputValue: t("admin.tenants.subscription.activate.reasonDefault"),
      showCancelButton: true,
      confirmButtonText: t("admin.tenants.subscription.activate.confirmSubmit"),
      cancelButtonText: t("admin.tenants.actions.cancel"),
      inputValidator: (value) => {
        if (value && String(value).trim().length > 500) {
          return t("admin.tenants.subscription.activate.reasonMax");
        }
        return null;
      },
    });

    if (!isConfirmed) {
      return;
    }

    setSubscriptionBusy(true);
    try {
      const response = await updateTenantSubscription(tenant.id, {
        status: "active",
        reason:
          String(reason ?? "").trim() ||
          t("admin.tenants.subscription.activate.reasonDefault"),
      });
      toast.success(
        response.message || t("admin.tenants.subscription.activate.toast.success")
      );
      await fetchTenants();
      await refreshDetailIfOpen(tenant.id);
    } catch (error: any) {
      toast.error(
        error?.data || t("admin.tenants.subscription.activate.toast.error")
      );
    } finally {
      setSubscriptionBusy(false);
    }
  };

  const handleSyncSubscriptionPayment = async (tenant: Tenant) => {
    const { value: reason, isConfirmed } = await Swal.fire({
      title: t("admin.tenants.subscription.sync.confirmTitle"),
      html: t("admin.tenants.subscription.sync.confirmBody", {
        name: tenant.name,
      }),
      input: "textarea",
      inputPlaceholder: t("admin.tenants.subscription.sync.reasonPlaceholder"),
      showCancelButton: true,
      confirmButtonText: t("admin.tenants.subscription.sync.confirmSubmit"),
      cancelButtonText: t("admin.tenants.actions.cancel"),
      inputValidator: (value) => {
        if (value && String(value).trim().length > 500) {
          return t("admin.tenants.subscription.activate.reasonMax");
        }
        return null;
      },
    });

    if (!isConfirmed) {
      return;
    }

    setSubscriptionBusy(true);
    try {
      const trimmed = String(reason ?? "").trim();
      const response = await syncTenantSubscriptionPayment(
        tenant.id,
        trimmed ? { reason: trimmed } : undefined
      );
      const activated = Boolean(response.meta?.activated);
      if (activated) {
        toast.success(
          response.message || t("admin.tenants.subscription.sync.toast.success")
        );
      } else {
        toast.info(
          response.message ||
            t("admin.tenants.subscription.sync.toast.unchanged")
        );
      }
      await fetchTenants();
      await refreshDetailIfOpen(tenant.id);
    } catch (error: any) {
      toast.error(
        error?.data || t("admin.tenants.subscription.sync.toast.error")
      );
    } finally {
      setSubscriptionBusy(false);
    }
  };

  const [nurtureOptOutBusy, setNurtureOptOutBusy] = useState(false);

  const handleToggleNurtureOptOut = async (tenant: Tenant, optOut: boolean) => {
    setNurtureOptOutBusy(true);
    try {
      const response = await updateTenantNurtureOptOut(tenant.id, optOut);
      toast.success(response.message);
      setDetailTenant((current) => {
        if (!current || current.id !== tenant.id) {
          return current;
        }
        return {
          ...current,
          activation_summary: {
            trial_started_at: current.activation_summary?.trial_started_at ?? null,
            trial_ends_at: current.activation_summary?.trial_ends_at ?? null,
            aha_completed_at: current.activation_summary?.aha_completed_at ?? null,
            concierge_completed_at:
              current.activation_summary?.concierge_completed_at ?? null,
            nurture_opt_out: response.data.nurture_opt_out,
            nurture_opted_out_at: response.data.nurture_opted_out_at,
          },
        };
      });
      await refreshDetailIfOpen(tenant.id);
    } catch (error: any) {
      toast.error(error?.data || t("admin.tenants.detail.nurtureOptOut.toastError"));
    } finally {
      setNurtureOptOutBusy(false);
    }
  };

  const createFormik = useFormik<CreateTenantFormValues>({
    enableReinitialize: true,
    initialValues: {
      studio_name: "",
      name: "",
      email: "",
      password: "",
      password_confirmation: "",
      plan_id: "",
      locale: "pt-BR",
      currency_code: "BRL",
      coupon_code: "",
    },
    validationSchema: createValidationSchema,
    onSubmit: async (values, { resetForm }) => {
      setCreating(true);

      try {
        const response = await createTenant({
          studio_name: values.studio_name.trim(),
          name: values.name.trim(),
          email: values.email.trim(),
          password: values.password,
          password_confirmation: values.password_confirmation,
          plan_id: Number(values.plan_id),
          locale: values.locale,
          currency_code: values.currency_code,
          coupon_code: values.coupon_code.trim() || undefined,
        });
        toast.success(response.message);
        setCreateModalOpen(false);
        resetForm();
        await fetchTenants();
      } catch (error: any) {
        toast.error(error?.data || t("admin.tenants.create.toast.error"));
      } finally {
        setCreating(false);
      }
    },
  });

  const closeCreateModal = () => {
    setCreateModalOpen(false);
    createFormik.resetForm();
  };

  const plansForCurrency = useMemo(
    () => plans.filter((plan) => plan.currency === createFormik.values.currency_code),
    [plans, createFormik.values.currency_code]
  );

  const blockFormik = useFormik<BlockFormValues>({
    enableReinitialize: true,
    initialValues: { block_reason: "" },
    validationSchema: blockValidationSchema,
    onSubmit: async (values) => {
      if (!blockTarget) {
        return;
      }

      setSubmitting(true);

      try {
        const response = await toggleTenantBlock(
          blockTarget.id,
          values.block_reason.trim()
        );
        toast.success(response.message);
        closeBlockModal();
        await fetchTenants();
      } catch (error: any) {
        toast.error(error?.data || t("admin.tenants.toast.blockError"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const closeBlockModal = () => {
    setBlockModalOpen(false);
    setBlockTarget(null);
    blockFormik.resetForm({ values: { block_reason: "" } });
  };

  const openBlockModal = (tenant: Tenant) => {
    setBlockTarget(tenant);
    blockFormik.resetForm({ values: { block_reason: "" } });
    setBlockModalOpen(true);
  };

  const confirmUnblock = async () => {
    if (!unblockTarget) {
      return;
    }

    setSubmitting(true);

    try {
      const response = await toggleTenantBlock(unblockTarget.id);
      toast.success(response.message);
      setUnblockModalOpen(false);
      setUnblockTarget(null);
      await fetchTenants();
    } catch (error: any) {
      toast.error(error?.data || t("admin.tenants.toast.unblockError"));
    } finally {
      setSubmitting(false);
    }
  };

  const closeImpersonateModal = () => {
    setImpersonateModalOpen(false);
    setImpersonateTarget(null);
  };

  const openImpersonateModal = (tenant: Tenant) => {
    setImpersonateTarget(tenant);
    setImpersonateModalOpen(true);
  };

  const confirmImpersonate = async () => {
    if (!impersonateTarget?.owner_user) {
      return;
    }

    const ownerUserId = impersonateTarget.owner_user.id;

    setImpersonatingUserId(ownerUserId);

    try {
      const response = await impersonate(ownerUserId);

      if (response.success && response.data?.access_token) {
        startImpersonation(response.data, impersonateTarget.name);
        window.location.href = "/dashboard";
        return;
      }

      toast.error(t("admin.tenants.impersonate.toast.error"));
    } catch (error: any) {
      toast.error(error?.data || t("admin.tenants.impersonate.toast.error"));
    } finally {
      setImpersonatingUserId(null);
    }
  };

  const totalPages = meta?.last_page ?? 1;

  const subscriptionStatusLabel = (status: TenantSubscriptionStatus): string =>
    t(`admin.tenants.subscriptionStatus.${status}`);

  return (
    <React.Fragment>
      <ToastContainer closeButton pauseOnHover />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("admin.tenants.breadcrumb.title")}
            pageTitle={t("admin.tenants.breadcrumb.pageTitle")}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center justify-content-between gap-2">
                  <h4 className="card-title mb-0">{t("admin.tenants.card.title")}</h4>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => setCreateModalOpen(true)}
                  >
                    <i className="ri-add-line align-middle me-1" />
                    {t("admin.tenants.actions.create")}
                  </Button>
                </CardHeader>

                <CardBody>
                  <Row className="g-2 mb-3 align-items-end">
                    <Col md={4} lg={3}>
                      <Label htmlFor="tenant-search" className="form-label">
                        {t("admin.tenants.filters.searchLabel")}
                      </Label>
                      <Input
                        id="tenant-search"
                        type="search"
                        value={searchInput}
                        placeholder={t("admin.tenants.filters.searchPlaceholder")}
                        onChange={(event) => setSearchInput(event.target.value)}
                      />
                    </Col>
                    <Col md={4} lg={2}>
                      <Label htmlFor="tenant-status-filter" className="form-label">
                        {t("admin.tenants.filters.subscriptionStatus")}
                      </Label>
                      <Input
                        id="tenant-status-filter"
                        type="select"
                        value={subscriptionStatusFilter}
                        onChange={(event) => {
                          setSubscriptionStatusFilter(
                            event.target.value as "" | TenantSubscriptionStatus
                          );
                          setPage(1);
                        }}
                      >
                        <option value="">
                          {t("admin.tenants.filters.allStatuses")}
                        </option>
                        <option value="active">
                          {t("admin.tenants.subscriptionStatus.active")}
                        </option>
                        <option value="past_due">
                          {t("admin.tenants.subscriptionStatus.past_due")}
                        </option>
                        <option value="suspended">
                          {t("admin.tenants.subscriptionStatus.suspended")}
                        </option>
                        <option value="canceled">
                          {t("admin.tenants.subscriptionStatus.canceled")}
                        </option>
                      </Input>
                    </Col>
                    <Col md={4} lg={2}>
                      <Label htmlFor="tenant-blocked-filter" className="form-label">
                        {t("admin.tenants.filters.blockStatus")}
                      </Label>
                      <Input
                        id="tenant-blocked-filter"
                        type="select"
                        value={blockedFilter}
                        onChange={(event) => {
                          setBlockedFilter(
                            event.target.value as TenantBlockedFilter
                          );
                          setPage(1);
                        }}
                      >
                        <option value="all">
                          {t("admin.tenants.filters.allBlockStatuses")}
                        </option>
                        <option value="operational">
                          {t("admin.tenants.blockStatus.operational")}
                        </option>
                        <option value="blocked">
                          {t("admin.tenants.blockStatus.blocked")}
                        </option>
                      </Input>
                    </Col>
                    <Col md={4} lg={2}>
                      <Label htmlFor="tenant-plan-filter" className="form-label">
                        {t("admin.tenants.filters.plan")}
                      </Label>
                      <Input
                        id="tenant-plan-filter"
                        type="select"
                        value={planFilter}
                        onChange={(event) => {
                          setPlanFilter(event.target.value);
                          setPage(1);
                        }}
                      >
                        <option value="">
                          {t("admin.tenants.filters.allPlans")}
                        </option>
                        {filterPlans.map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            {plan.name} ({plan.currency})
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={4} lg={2}>
                      <Label htmlFor="tenant-sort" className="form-label">
                        {t("admin.tenants.filters.sort")}
                      </Label>
                      <Input
                        id="tenant-sort"
                        type="select"
                        value={`${sort}:${order}`}
                        onChange={(event) => {
                          const [nextSort, nextOrder] = event.target.value.split(
                            ":"
                          ) as [TenantListSort, TenantListOrder];
                          setSort(nextSort);
                          setOrder(nextOrder);
                          setPage(1);
                        }}
                      >
                        <option value="created_at:desc">
                          {t("admin.tenants.filters.sortCreatedDesc")}
                        </option>
                        <option value="created_at:asc">
                          {t("admin.tenants.filters.sortCreatedAsc")}
                        </option>
                        <option value="name:asc">
                          {t("admin.tenants.filters.sortNameAsc")}
                        </option>
                        <option value="name:desc">
                          {t("admin.tenants.filters.sortNameDesc")}
                        </option>
                        <option value="last_login_at:desc">
                          {t("admin.tenants.filters.sortLastLoginDesc")}
                        </option>
                        <option value="last_login_at:asc">
                          {t("admin.tenants.filters.sortLastLoginAsc")}
                        </option>
                      </Input>
                    </Col>
                    <Col md={4} lg={1} className="d-flex">
                      <Button
                        color="light"
                        className="w-100"
                        disabled={!hasActiveFilters && sort === "created_at" && order === "desc"}
                        onClick={clearFilters}
                      >
                        {t("admin.tenants.filters.clear")}
                      </Button>
                    </Col>
                  </Row>
                  <p className="text-muted small mb-3">
                    {t("admin.tenants.inactive.legend", {
                      days: INACTIVE_LOGIN_DAYS,
                    })}
                  </p>

                  {loading ? (
                    <div className="d-flex justify-content-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : tenants.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <p className="mb-0">
                        {hasActiveFilters
                          ? t("admin.tenants.empty.noResults")
                          : t("admin.tenants.empty.noData")}
                      </p>
                      {hasActiveFilters ? (
                        <Button
                          color="link"
                          className="mt-2 p-0"
                          onClick={clearFilters}
                        >
                          {t("admin.tenants.filters.clear")}
                        </Button>
                      ) : null}
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table hover className="align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>
                                <button
                                  type="button"
                                  className="btn btn-link p-0 text-body fw-semibold text-decoration-none"
                                  onClick={() => toggleSort("name")}
                                >
                                  {t("admin.tenants.table.name")}{" "}
                                  <i className={`${sortIndicator("name")} align-middle`} />
                                </button>
                              </th>
                              <th>{t("admin.tenants.table.owner")}</th>
                              <th>{t("admin.tenants.table.plan")}</th>
                              <th>{t("admin.tenants.table.subscriptionStatus")}</th>
                              <th>
                                <button
                                  type="button"
                                  className="btn btn-link p-0 text-body fw-semibold text-decoration-none"
                                  onClick={() => toggleSort("created_at")}
                                >
                                  {t("admin.tenants.table.createdAt")}{" "}
                                  <i
                                    className={`${sortIndicator("created_at")} align-middle`}
                                  />
                                </button>
                              </th>
                              <th>
                                <button
                                  type="button"
                                  className="btn btn-link p-0 text-body fw-semibold text-decoration-none"
                                  onClick={() => toggleSort("last_login_at")}
                                >
                                  {t("admin.tenants.table.lastLogin")}{" "}
                                  <i
                                    className={`${sortIndicator(
                                      "last_login_at"
                                    )} align-middle`}
                                  />
                                </button>
                              </th>
                              <th>{t("admin.tenants.table.counts")}</th>
                              <th>{t("admin.tenants.table.blockStatus")}</th>
                              <th className="text-end" style={{ width: 240 }}>
                                {t("admin.tenants.table.actions")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {tenants.map((tenant) => {
                              const blocked = isTenantBlocked(tenant);
                              const subscription = tenant.subscription;
                              const inactive = isInactiveLogin(tenant);
                              const courtesyActive =
                                tenant.courtesy_until != null &&
                                new Date(tenant.courtesy_until).getTime() >
                                  Date.now();

                              return (
                                <tr
                                  key={tenant.id}
                                  className={inactive ? "table-warning" : undefined}
                                  title={
                                    inactive
                                      ? t("admin.tenants.inactive.rowHint", {
                                          days: INACTIVE_LOGIN_DAYS,
                                        })
                                      : undefined
                                  }
                                >
                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-link p-0 fw-medium text-decoration-none"
                                      onClick={() => openTenantDetail(tenant)}
                                    >
                                      {tenant.name}
                                    </button>
                                    <div className="text-muted small">
                                      {tenant.document || notAvailable}
                                    </div>
                                    <div className="d-flex flex-wrap gap-1 mt-1">
                                      {inactive ? (
                                        <Badge color="warning" className="fs-12">
                                          {t("admin.tenants.inactive.badge", {
                                            days: INACTIVE_LOGIN_DAYS,
                                          })}
                                        </Badge>
                                      ) : null}
                                      {tenant.is_demo ? (
                                        <Badge color="info" className="fs-12">
                                          {t("admin.tenants.courtesy.demoOn")}
                                        </Badge>
                                      ) : null}
                                      {courtesyActive ? (
                                        <Badge color="soft-warning" className="fs-12">
                                          {t("admin.tenants.courtesy.active")}
                                        </Badge>
                                      ) : null}
                                    </div>
                                  </td>
                                  <td>
                                    {tenant.owner_user ? (
                                      <>
                                        <div className="fw-medium">
                                          {tenant.owner_user.name}
                                        </div>
                                        <div className="text-muted small">
                                          {tenant.owner_user.email}
                                        </div>
                                      </>
                                    ) : (
                                      <span className="text-muted">
                                        {notAvailable}
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    {subscription?.plan?.name ?? notAvailable}
                                  </td>
                                  <td>
                                    {subscription ? (
                                      <Badge
                                        color={subscriptionBadgeColor(subscription.status)}
                                        className="fs-12"
                                      >
                                        {subscriptionStatusLabel(subscription.status)}
                                      </Badge>
                                    ) : (
                                      <span className="text-muted">{notAvailable}</span>
                                    )}
                                  </td>
                                  <td className="text-muted">
                                    {formatDateTime(tenant.created_at)}
                                  </td>
                                  <td className="text-muted">
                                    {formatDateTime(tenant.last_login_at)}
                                  </td>
                                  <td className="text-muted">
                                    {t("admin.tenants.table.countsValue", {
                                      units: tenant.units_count,
                                      professionals: tenant.professionals_count,
                                    })}
                                  </td>
                                  <td>
                                    <Badge
                                      color={blocked ? "danger" : "success"}
                                      className="fs-12"
                                      title={
                                        blocked && tenant.blocked_at
                                          ? t("admin.tenants.blockStatus.blockedSince", {
                                              date: formatDateTime(tenant.blocked_at),
                                            })
                                          : undefined
                                      }
                                    >
                                      {blocked
                                        ? t("admin.tenants.blockStatus.blocked")
                                        : t("admin.tenants.blockStatus.operational")}
                                    </Badge>
                                    {blocked && tenant.blocked_at ? (
                                      <div className="text-muted small mt-1">
                                        {t("admin.tenants.blockStatus.blockedSince", {
                                          date: formatDateTime(tenant.blocked_at),
                                        })}
                                      </div>
                                    ) : null}
                                    {blocked && tenant.block_reason ? (
                                      <div
                                        className="text-muted small mt-1 text-truncate"
                                        style={{ maxWidth: 160 }}
                                        title={tenant.block_reason}
                                      >
                                        {tenant.block_reason}
                                      </div>
                                    ) : null}
                                  </td>
                                  <td className="text-end">
                                    <div className="d-flex flex-wrap justify-content-end gap-1">
                                      <Button
                                        color="soft-secondary"
                                        size="sm"
                                        title={t("admin.tenants.actions.viewDetails")}
                                        onClick={() => openTenantDetail(tenant)}
                                      >
                                        <i className="ri-eye-line align-bottom me-1" />
                                        {t("admin.tenants.actions.viewDetails")}
                                      </Button>
                                      {(() => {
                                        const eligible = isImpersonationEligible(tenant);
                                        const unavailableKey =
                                          impersonationUnavailableKey(tenant);
                                        const isLoading =
                                          impersonatingUserId ===
                                          tenant.owner_user?.id;

                                        return (
                                          <Button
                                            color="soft-primary"
                                            size="sm"
                                            title={
                                              unavailableKey
                                                ? t(unavailableKey)
                                                : t("admin.tenants.impersonate.action")
                                            }
                                            disabled={!eligible || isLoading}
                                            onClick={() => openImpersonateModal(tenant)}
                                          >
                                            {isLoading ? (
                                              <Spinner size="sm" className="me-1" />
                                            ) : (
                                              <i className="ri-shield-user-line align-bottom me-1" />
                                            )}
                                            {t("admin.tenants.impersonate.action")}
                                          </Button>
                                        );
                                      })()}
                                      {blocked ? (
                                        <Button
                                          color="soft-success"
                                          size="sm"
                                          title={t("admin.tenants.actions.unblock")}
                                          onClick={() => {
                                            setUnblockTarget(tenant);
                                            setUnblockModalOpen(true);
                                          }}
                                        >
                                          <i className="ri-lock-unlock-line align-bottom me-1" />
                                          {t("admin.tenants.actions.unblock")}
                                        </Button>
                                      ) : (
                                        <Button
                                          color="soft-danger"
                                          size="sm"
                                          title={t("admin.tenants.actions.block")}
                                          onClick={() => openBlockModal(tenant)}
                                        >
                                          <i className="ri-forbid-line align-bottom me-1" />
                                          {t("admin.tenants.actions.block")}
                                        </Button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>

                      {meta && totalPages > 1 && (
                        <Row className="align-items-center mt-3 g-3">
                          <Col sm={6}>
                            <p className="text-muted mb-0">
                              {t("admin.tenants.pagination.showing", {
                                from: meta.from ?? 0,
                                to: meta.to ?? 0,
                                total: meta.total,
                              })}
                            </p>
                          </Col>
                          <Col sm={6}>
                            <Pagination className="pagination-separated justify-content-sm-end mb-0">
                              <PaginationItem disabled={page <= 1}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page > 1) {
                                      setPage(page - 1);
                                    }
                                  }}
                                >
                                  {t("admin.tenants.pagination.previous")}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem active>
                                <PaginationLink href="#">
                                  {page} / {totalPages}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem disabled={page >= totalPages}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page < totalPages) {
                                      setPage(page + 1);
                                    }
                                  }}
                                >
                                  {t("admin.tenants.pagination.next")}
                                </PaginationLink>
                              </PaginationItem>
                            </Pagination>
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={blockModalOpen} toggle={closeBlockModal} centered>
        <ModalHeader toggle={closeBlockModal}>
          {t("admin.tenants.blockModal.title")}
        </ModalHeader>
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            blockFormik.handleSubmit();
          }}
        >
          <ModalBody>
            <p className="text-muted">
              {blockTarget
                ? t("admin.tenants.blockModal.description", {
                    name: blockTarget.name,
                  })
                : t("admin.tenants.blockModal.fallback")}
            </p>
            <div className="mb-0">
              <Label htmlFor="tenant-block-reason">
                {t("admin.tenants.fields.blockReason.label")}
              </Label>
              <Input
                id="tenant-block-reason"
                name="block_reason"
                type="textarea"
                rows={3}
                placeholder={t("admin.tenants.fields.blockReason.placeholder")}
                value={blockFormik.values.block_reason}
                onChange={blockFormik.handleChange}
                onBlur={blockFormik.handleBlur}
                invalid={
                  !!(
                    blockFormik.touched.block_reason &&
                    blockFormik.errors.block_reason
                  )
                }
              />
              {blockFormik.touched.block_reason &&
              blockFormik.errors.block_reason ? (
                <FormFeedback type="invalid">
                  {blockFormik.errors.block_reason}
                </FormFeedback>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="light" type="button" onClick={closeBlockModal}>
              {t("admin.tenants.actions.cancel")}
            </Button>
            <Button color="danger" type="submit" disabled={submitting}>
              {submitting ? (
                <Spinner size="sm" className="me-1" />
              ) : (
                <i className="ri-forbid-line align-bottom me-1" />
              )}
              {t("admin.tenants.actions.confirmBlock")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal
        isOpen={unblockModalOpen}
        toggle={() => setUnblockModalOpen(false)}
        centered
      >
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <i className="ri-lock-unlock-line display-5 text-success" />
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t("admin.tenants.unblockModal.title")}</h4>
              <p className="text-muted mx-4 mb-0">
                {unblockTarget
                  ? t("admin.tenants.unblockModal.description", {
                      name: unblockTarget.name,
                    })
                  : t("admin.tenants.unblockModal.fallback")}
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <Button
              color="light"
              onClick={() => {
                setUnblockModalOpen(false);
                setUnblockTarget(null);
              }}
            >
              {t("admin.tenants.actions.cancel")}
            </Button>
            <Button color="success" onClick={confirmUnblock} disabled={submitting}>
              {submitting ? (
                <Spinner size="sm" className="me-1" />
              ) : null}
              {t("admin.tenants.actions.confirmUnblock")}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={impersonateModalOpen}
        toggle={closeImpersonateModal}
        centered
      >
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <i className="ri-shield-user-line display-5 text-primary" />
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t("admin.tenants.impersonate.confirm.title")}</h4>
              <p className="text-muted mx-4 mb-0">
                {impersonateTarget?.owner_user
                  ? t("admin.tenants.impersonate.confirm.description", {
                      tenantName: impersonateTarget.name,
                      ownerName: impersonateTarget.owner_user.name,
                      ownerEmail: impersonateTarget.owner_user.email,
                    })
                  : t("admin.tenants.impersonate.confirm.fallback")}
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <Button color="light" onClick={closeImpersonateModal}>
              {t("admin.tenants.actions.cancel")}
            </Button>
            <Button
              color="primary"
              onClick={confirmImpersonate}
              disabled={
                impersonatingUserId !== null || !impersonateTarget?.owner_user
              }
            >
              {impersonatingUserId !== null ? (
                <Spinner size="sm" className="me-1" />
              ) : (
                <i className="ri-login-box-line align-bottom me-1" />
              )}
              {t("admin.tenants.impersonate.confirm.submit")}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <TenantDetailOffcanvas
        isOpen={detailOpen}
        loading={detailLoading}
        tenant={detailTenant}
        notAvailable={notAvailable}
        formatDateTime={formatDateTime}
        subscriptionStatusLabel={subscriptionStatusLabel}
        subscriptionBadgeColor={subscriptionBadgeColor}
        onClose={closeTenantDetail}
        onImpersonate={(tenant) => {
          closeTenantDetail();
          openImpersonateModal(tenant);
        }}
        onBlock={(tenant) => {
          closeTenantDetail();
          openBlockModal(tenant);
        }}
        onUnblock={(tenant) => {
          closeTenantDetail();
          setUnblockTarget(tenant);
          setUnblockModalOpen(true);
        }}
        onExtendCourtesy={handleExtendCourtesy}
        onToggleDemo={handleToggleDemo}
        onClearCourtesy={handleClearCourtesy}
        onMarkSubscriptionPaid={handleMarkSubscriptionPaid}
        onSyncSubscriptionPayment={handleSyncSubscriptionPayment}
        onToggleNurtureOptOut={handleToggleNurtureOptOut}
        nurtureOptOutBusy={nurtureOptOutBusy}
        subscriptionBusy={subscriptionBusy}
        canImpersonate={isImpersonationEligible}
        impersonateUnavailableKey={impersonationUnavailableKey}
        impersonatingUserId={impersonatingUserId}
      />

      <Modal isOpen={createModalOpen} toggle={closeCreateModal} centered size="lg">
        <ModalHeader toggle={closeCreateModal}>
          {t("admin.tenants.create.title")}
        </ModalHeader>
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            createFormik.handleSubmit();
          }}
        >
          <ModalBody>
            <p className="text-muted">{t("admin.tenants.create.description")}</p>
            <Row className="g-3">
              <Col md={6}>
                <Label htmlFor="create-studio-name">
                  {t("admin.tenants.create.fields.studioName")}
                </Label>
                <Input
                  id="create-studio-name"
                  name="studio_name"
                  value={createFormik.values.studio_name}
                  onChange={createFormik.handleChange}
                  onBlur={createFormik.handleBlur}
                  invalid={
                    !!(
                      createFormik.touched.studio_name &&
                      createFormik.errors.studio_name
                    )
                  }
                />
                {createFormik.touched.studio_name &&
                createFormik.errors.studio_name ? (
                  <FormFeedback>{createFormik.errors.studio_name}</FormFeedback>
                ) : null}
              </Col>
              <Col md={6}>
                <Label htmlFor="create-owner-name">
                  {t("admin.tenants.create.fields.ownerName")}
                </Label>
                <Input
                  id="create-owner-name"
                  name="name"
                  value={createFormik.values.name}
                  onChange={createFormik.handleChange}
                  onBlur={createFormik.handleBlur}
                  invalid={
                    !!(createFormik.touched.name && createFormik.errors.name)
                  }
                />
                {createFormik.touched.name && createFormik.errors.name ? (
                  <FormFeedback>{createFormik.errors.name}</FormFeedback>
                ) : null}
              </Col>
              <Col md={6}>
                <Label htmlFor="create-owner-email">
                  {t("admin.tenants.create.fields.email")}
                </Label>
                <Input
                  id="create-owner-email"
                  name="email"
                  type="email"
                  value={createFormik.values.email}
                  onChange={createFormik.handleChange}
                  onBlur={createFormik.handleBlur}
                  invalid={
                    !!(createFormik.touched.email && createFormik.errors.email)
                  }
                />
                {createFormik.touched.email && createFormik.errors.email ? (
                  <FormFeedback>{createFormik.errors.email}</FormFeedback>
                ) : null}
              </Col>
              <Col md={6}>
                <Label htmlFor="create-coupon">
                  {t("admin.tenants.create.fields.coupon")}
                </Label>
                <Input
                  id="create-coupon"
                  name="coupon_code"
                  value={createFormik.values.coupon_code}
                  onChange={createFormik.handleChange}
                  onBlur={createFormik.handleBlur}
                  placeholder={t("admin.tenants.create.fields.couponPlaceholder")}
                />
              </Col>
              <Col md={6}>
                <Label htmlFor="create-password">
                  {t("admin.tenants.create.fields.password")}
                </Label>
                <Input
                  id="create-password"
                  name="password"
                  type="password"
                  value={createFormik.values.password}
                  onChange={createFormik.handleChange}
                  onBlur={createFormik.handleBlur}
                  invalid={
                    !!(
                      createFormik.touched.password &&
                      createFormik.errors.password
                    )
                  }
                />
                {createFormik.touched.password &&
                createFormik.errors.password ? (
                  <FormFeedback>{createFormik.errors.password}</FormFeedback>
                ) : null}
              </Col>
              <Col md={6}>
                <Label htmlFor="create-password-confirmation">
                  {t("admin.tenants.create.fields.passwordConfirmation")}
                </Label>
                <Input
                  id="create-password-confirmation"
                  name="password_confirmation"
                  type="password"
                  value={createFormik.values.password_confirmation}
                  onChange={createFormik.handleChange}
                  onBlur={createFormik.handleBlur}
                  invalid={
                    !!(
                      createFormik.touched.password_confirmation &&
                      createFormik.errors.password_confirmation
                    )
                  }
                />
                {createFormik.touched.password_confirmation &&
                createFormik.errors.password_confirmation ? (
                  <FormFeedback>
                    {createFormik.errors.password_confirmation}
                  </FormFeedback>
                ) : null}
              </Col>
              <Col md={4}>
                <Label htmlFor="create-currency">
                  {t("admin.tenants.create.fields.currency")}
                </Label>
                <Input
                  id="create-currency"
                  name="currency_code"
                  type="select"
                  value={createFormik.values.currency_code}
                  onChange={(event) => {
                    createFormik.handleChange(event);
                    createFormik.setFieldValue("plan_id", "");
                  }}
                >
                  <option value="BRL">BRL</option>
                  <option value="USD">USD</option>
                </Input>
              </Col>
              <Col md={4}>
                <Label htmlFor="create-locale">
                  {t("admin.tenants.create.fields.locale")}
                </Label>
                <Input
                  id="create-locale"
                  name="locale"
                  type="select"
                  value={createFormik.values.locale}
                  onChange={createFormik.handleChange}
                >
                  <option value="pt-BR">pt-BR</option>
                  <option value="en">en</option>
                  <option value="es">es</option>
                </Input>
              </Col>
              <Col md={4}>
                <Label htmlFor="create-plan">
                  {t("admin.tenants.create.fields.plan")}
                </Label>
                <Input
                  id="create-plan"
                  name="plan_id"
                  type="select"
                  value={createFormik.values.plan_id}
                  onChange={createFormik.handleChange}
                  onBlur={createFormik.handleBlur}
                  invalid={
                    !!(
                      createFormik.touched.plan_id && createFormik.errors.plan_id
                    )
                  }
                >
                  <option value="">
                    {t("admin.tenants.create.fields.planPlaceholder")}
                  </option>
                  {plansForCurrency.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} ({plan.currency})
                    </option>
                  ))}
                </Input>
                {createFormik.touched.plan_id && createFormik.errors.plan_id ? (
                  <FormFeedback>{createFormik.errors.plan_id}</FormFeedback>
                ) : null}
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="light" type="button" onClick={closeCreateModal}>
              {t("admin.tenants.actions.cancel")}
            </Button>
            <Button color="primary" type="submit" disabled={creating}>
              {creating ? <Spinner size="sm" className="me-1" /> : null}
              {t("admin.tenants.create.submit")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default SuperAdminTenants;
